import { parseEntityRef } from '@backstage/catalog-model';
import type { Loaded } from '../catalog/pipeline/useInsights';
import type { ListState } from './pullRequestLists';
import type { CiHealthSnapshot, RepoCiHealth } from './ciSnapshot';
import type { OwnedComponent, Reader } from './homeSources';
import { updatedLabel } from './updatedLabel';

/**
 * What the Needs attention card lists, and in what order (DX-307).
 *
 * A pure function of the page's sources, so the selection — which is the
 * part that has to be defensible — can be proved without drawing anything
 * (specs/needs-attention.md).
 *
 * The rules, each of them a promise in that spec:
 *   - Every row carries one action, and the action reaches a page that exists.
 *     A row whose destination is not built yet is left out rather than
 *     linking into nothing: there is no decisions row before the ADR index
 *     (DX-310). The Status page (DX-309) is built, but no incident row reads
 *     it yet.
 *   - At most two rows from any one source, so no source can crowd out the
 *     others; when a source has more, its second row says how many and leads to
 *     where they are all listed. Four sources, one of them a single row at
 *     most, so the card never holds more than seven.
 *   - The reader's own work first, then by how many people it holds up: a
 *     failing default branch of their team's stops everyone shipping from it;
 *     a review waiting on them stops one colleague; a component whose catalog
 *     entry is wrong stops nobody today. A reader in no team cannot be told
 *     which builds are theirs, so they get the platform's failing builds last.
 *     Why is said once, by the team card beside the figures, not by a row here
 *     (DX-546; specs/home-page.md AC-29).
 */

/** Where a row came from. */
export type AttentionSource = 'builds' | 'reviews' | 'catalog';

/** How the row is marked, which is what kind of thing needs attention. */
export type AttentionTone = 'failure' | 'review' | 'defect';

/** The one thing a row lets the reader do. */
export type AttentionAction =
  /** A page outside the portal — GitHub — opened in a new tab. */
  | { kind: 'external'; label: string; href: string }
  /** A page of the portal's own. */
  | { kind: 'internal'; label: string; to: string }
  /** A card further down the home page, which the row scrolls to. */
  | { kind: 'in-page'; label: string; target: 'reviews' | 'builds' };

export type AttentionRow = {
  id: string;
  /**
   * How many open items the row stands for: one, or for a row summarising the
   * rest of a source, how many it summarises. The card's count adds these up,
   * so a row reading "19 more" counts as nineteen, not one.
   */
  count: number;
  source: AttentionSource;
  tone: AttentionTone;
  /** The title, with the parts set as code (a branch name) marked as such. */
  title: Array<string | { code: string }>;
  detail: string;
  /** When the thing happened, where the source says. */
  when?: { label: string; exact: string };
  action: AttentionAction;
};

/** A source the card could not ask, and why — said on the card. */
export type Unchecked = {
  source: AttentionSource;
  /** What could not be checked, as the card names it. */
  what: string;
  reason: string;
};

export type Attention = {
  rows: AttentionRow[];
  /** Sources still being asked. */
  pending: AttentionSource[];
  /** Sources that could not be asked, so the list may be incomplete. */
  unchecked: Unchecked[];
  /**
   * For a reader in a team: how many repositories fail on their default branch
   * that are not the team's, and so are not listed. CI health below marks
   * them red, and a card that left them out without a word would read as
   * contradicting it (specs/needs-attention.md AC-6). Zero for a reader in no
   * team, whose card lists the platform's builds themselves.
   */
  elsewhere: number;
};

export type AttentionInputs = {
  reader: Loaded<Reader>;
  owned: Loaded<OwnedComponent[]>;
  unresolved: Loaded<Record<string, string[]>>;
  ci: Loaded<CiHealthSnapshot>;
  reviews: ListState;
  now?: number;
};

/** At most this many rows from any one source. */
export const ROWS_PER_SOURCE = 2;

/** The card's upper bound: two each from three sources. */
export const MAX_ROWS = 6;

export const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const whenOf = (iso: string | null | undefined, now: number, prefix = '') => {
  if (!iso) return undefined;
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return undefined;
  return {
    label: `${prefix}${updatedLabel(iso, now)}`,
    exact: at.toLocaleString(),
  };
};

/** A row as a source's builder makes it, before it is counted. */
type RowBody = Omit<AttentionRow, 'count'>;

/**
 * Takes the first row of a source and, when there are more than two, a second
 * row that says how many more — rather than a second item and silence about the
 * rest, which would let a source with twenty items read as one with two.
 *
 * `total` is how many items the source holds, when that is more than it listed:
 * the review list names at most fifty, and the summary row has to agree with the
 * figure that counts all of them.
 */
const capped = <T>(
  items: T[],
  row: (item: T) => RowBody,
  more: (count: number) => RowBody,
  total = items.length,
): AttentionRow[] => {
  if (items.length === 0) return [];
  if (total <= ROWS_PER_SOURCE) {
    return items.map(item => ({ ...row(item), count: 1 }));
  }
  return [
    { ...row(items[0]), count: 1 },
    { ...more(total - 1), count: total - 1 },
  ];
};

/** The repository's name without its organisation, as a reader says it. */
const repoName = (slug: string) => slug.slice(slug.indexOf('/') + 1);

const buildRow = (
  repo: RepoCiHealth,
  windowDays: number | undefined,
  now: number,
): RowBody => {
  const branch = repo.lastRun?.headBranch;
  const passed = Math.round(repo.successRate * 100);
  const window = windowDays ? ` in the last ${windowDays} days` : '';
  return {
    id: `builds:${repo.slug}`,
    source: 'builds',
    tone: 'failure',
    title: branch
      ? [{ code: branch }, ` is failing in ${repoName(repo.slug)}`]
      : [`The default branch is failing in ${repoName(repo.slug)}`],
    detail: `${repo.slug} · ${passed}% of its finished runs passed${window}`,
    when: whenOf(repo.lastRun?.createdAt, now),
    // Only rows with a run to show are made at all — see `failingBuilds`.
    action: {
      kind: 'external',
      label: 'View run',
      href: repo.lastRun!.htmlUrl,
    },
  };
};

const moreBuildsRow = (count: number): RowBody => ({
  id: 'builds:more',
  source: 'builds',
  tone: 'failure',
  title: [
    `${plural(
      count,
      'more repository fails',
      'more repositories fail',
    )} on the default branch`,
  ],
  detail: 'CI health below lists every one, failing first.',
  action: { kind: 'in-page', label: 'View all', target: 'builds' },
});

/**
 * The failing default branches the card may list, most recent failure first —
 * the one whose cause is freshest, and so cheapest to find.
 *
 * A repository with no run to point at is left out: its row would have no
 * destination, and a row with a broken action is worse than no row.
 */
const failingBuilds = (snapshot: CiHealthSnapshot, scope?: Set<string>) =>
  snapshot.repos
    .filter(r => r.isRed && r.lastRun?.htmlUrl)
    .filter(r => !scope || scope.has(r.slug.toLowerCase()))
    .sort((a, b) =>
      (b.lastRun?.createdAt ?? '').localeCompare(a.lastRun?.createdAt ?? ''),
    );

const reviewRow = (
  pr: NonNullable<ListState['data']>['pullRequests'][number],
  now: number,
): RowBody => ({
  id: `reviews:${pr.htmlUrl}`,
  source: 'reviews',
  tone: 'review',
  title: [
    `${pr.repo ? `${pr.repo} ` : ''}#${pr.number} is waiting on your review`,
  ],
  detail: pr.author ? `${pr.title} · by ${pr.author}` : pr.title,
  when: whenOf(pr.createdAt, now, 'opened '),
  action: { kind: 'external', label: 'Open PR', href: pr.htmlUrl },
});

const moreReviewsRow = (count: number): RowBody => ({
  id: 'reviews:more',
  source: 'reviews',
  tone: 'review',
  title: [
    `${plural(
      count,
      'more pull request waits',
      'more pull requests wait',
    )} on your review`,
  ],
  detail: 'Your pull requests below list every one.',
  action: { kind: 'in-page', label: 'View all', target: 'reviews' },
});

/** "zitadel, scaleway-ai, intelligence and 1 more" — the names, not the refs. */
const namesOf = (refs: string[]) => {
  const names = refs.map(ref => {
    try {
      return parseEntityRef(ref).name;
    } catch {
      return ref;
    }
  });
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
};

const defectRow = (component: OwnedComponent, missing: string[]): RowBody => ({
  id: `catalog:${component.ref}`,
  source: 'catalog',
  tone: 'defect',
  title: [
    `${component.title}: ${plural(
      missing.length,
      'catalog relation',
      'catalog relations',
    )} unresolved`,
  ],
  detail: `Not in the catalog: ${namesOf(missing)}`,
  action: {
    kind: 'internal',
    label: 'Review metadata',
    to: `/catalog/${component.namespace}/component/${component.name}`,
  },
});

const moreDefectsRow = (count: number): RowBody => ({
  id: 'catalog:more',
  source: 'catalog',
  tone: 'defect',
  title: [
    `${plural(
      count,
      'more of your components has',
      'more of your components have',
    )} unresolved relations`,
  ],
  detail: 'Each one says which on its own page.',
  action: {
    kind: 'internal',
    label: 'View yours',
    to: '/catalog?filters%5Bkind%5D=component&filters%5Buser%5D=owned',
  },
});

export const attentionOf = ({
  reader,
  owned,
  unresolved,
  ci,
  reviews,
  now = Date.now(),
}: AttentionInputs): Attention => {
  const pending: AttentionSource[] = [];
  const unchecked: Unchecked[] = [];

  // ── Builds ────────────────────────────────────────────────────────────────
  // Whose builds: the team's when the reader is in one, the platform's when
  // they are in none — known only once both the reader and, for a team, the
  // repositories it owns have answered.
  let builds: AttentionRow[] = [];
  let buildsArePlatform = false;
  let elsewhere = 0;
  if (ci.loading || reader.loading || (reader.data?.inTeam && owned.loading)) {
    pending.push('builds');
  } else if (ci.error || !ci.data) {
    unchecked.push({
      source: 'builds',
      what: 'builds',
      reason: 'the CI snapshot could not be loaded',
    });
  } else if (!ci.data.generatedAt) {
    unchecked.push({
      source: 'builds',
      what: 'builds',
      reason: 'the first CI snapshot is still being collected',
    });
  } else if (reader.error || !reader.data) {
    unchecked.push({
      source: 'builds',
      what: 'builds',
      reason: 'your sign-in could not be read',
    });
  } else if (reader.data.inTeam && (owned.error || !owned.data)) {
    unchecked.push({
      source: 'builds',
      what: 'builds',
      reason: 'the catalog could not say which repositories are your team’s',
    });
  } else {
    const snapshot = ci.data;
    buildsArePlatform = !reader.data.inTeam;
    const scope = reader.data.inTeam
      ? new Set(
          (owned.data ?? [])
            .map(c => c.slug)
            .filter((s): s is string => Boolean(s)),
        )
      : undefined;
    builds = capped(
      failingBuilds(snapshot, scope),
      repo => buildRow(repo, snapshot.windowDays, now),
      moreBuildsRow,
    );
    // Counted the way CI health marks them — every repository the snapshot
    // marks red — so the two add up on the screen.
    if (scope) {
      elsewhere = snapshot.repos.filter(
        r => r.isRed && !scope.has(r.slug.toLowerCase()),
      ).length;
    }
  }

  // ── Reviews waiting on the reader ─────────────────────────────────────────
  let reviewRows: AttentionRow[] = [];
  if (reviews.loading) {
    pending.push('reviews');
  } else if (reviews.error || !reviews.data) {
    unchecked.push({
      source: 'reviews',
      what: 'reviews',
      reason: 'your review requests could not be loaded',
    });
  } else if (!reviews.data.mapped) {
    unchecked.push({
      source: 'reviews',
      what: 'reviews',
      reason: 'no GitHub account is linked to your user',
    });
  } else {
    // A draft is waiting on nobody but its author (specs/home-page.md AC-11).
    const ready = reviews.data.pullRequests.filter(pr => !pr.isDraft);
    // Counted from the source's total, less the drafts it listed, so the
    // summary row does not stop at the fifty the list holds. It is not the
    // open-reviews figure: that counts drafts, which the card leaves out
    // (specs/needs-attention.md, Gaps). A draft beyond the fifty cannot be told
    // apart and is counted.
    const drafts = reviews.data.pullRequests.length - ready.length;
    reviewRows = capped(
      ready,
      pr => reviewRow(pr, now),
      moreReviewsRow,
      Math.max(ready.length, reviews.data.total - drafts),
    );
    const missing = reviews.data.unreachable ?? [];
    if (missing.length > 0) {
      unchecked.push({
        source: 'reviews',
        what: `reviews in ${missing.join(', ')}`,
        reason:
          missing.length === 1
            ? 'the organisation could not be asked'
            : 'the organisations could not be asked',
      });
    }
  }

  // ── The reader's components in the catalog ───────────────────────────────
  let defects: AttentionRow[] = [];
  if (owned.loading || unresolved.loading) {
    pending.push('catalog');
  } else if (
    owned.error ||
    unresolved.error ||
    !owned.data ||
    !unresolved.data
  ) {
    unchecked.push({
      source: 'catalog',
      what: 'your catalog entries',
      reason: 'the catalog could not be asked',
    });
  } else {
    const byComponent = unresolved.data;
    const withDefects = owned.data
      .filter(c => (byComponent[c.ref]?.length ?? 0) > 0)
      .sort(
        (a, b) =>
          byComponent[b.ref].length - byComponent[a.ref].length ||
          a.title.localeCompare(b.title),
      );
    defects = capped(
      withDefects,
      c => defectRow(c, byComponent[c.ref]),
      moreDefectsRow,
    );
  }

  const rows = buildsArePlatform
    ? [...reviewRows, ...defects, ...builds]
    : [...builds, ...reviewRows, ...defects];

  return { rows: rows.slice(0, MAX_ROWS), pending, unchecked, elsewhere };
};
